import React from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { ArrowRightIcon, BatteryWarningIcon, CheckIcon, ClockIcon, MapIcon, MapPinIcon, NavigationIcon, SparklesIcon, Trash2Icon, TruckIcon } from 'lucide-react';
import { citizenAddress, collections, liveCollection, materials } from '../../data/collectes';
import { profileCoordinates } from '../../data/demandes';
import { batteryDropOffs } from '../../data/plan';
import { DropOffMap } from './DropOffMap';
import { binRequest, binRequestReference } from '../../data/binRequest';
import type { CollecteKind } from '../../hooks/useCollecteConversation';
import { useLiveTruck } from '../../hooks/useLiveTruck';
import { NotifyFollowUp } from '../demandes/NotifyFollowUp';
import { TruckPreviewMap } from './TruckPreviewMap';
interface CollecteMessageProps {
  kind: CollecteKind;
  live: boolean;
  passedAt: string | null;
  onConfirmPickedUp: () => void;
  onReportNotPickedUp: () => void;
  onFindNearest: () => void;
}

/** Points de dépôt triés du plus proche au plus éloigné du domicile. */
const sortedDropOffs = batteryDropOffs.map(place => ({
  place,
  meters: L.latLng(profileCoordinates).distanceTo([place.lat, place.lng])
})).sort((a, b) => a.meters - b.meters);

/** Prochaine collecte, reprise de la liste pour ne jamais la contredire. */
const nextCollectionLabel = `${collections[1].weekday.toLowerCase()} ${collections[1].date}`;
function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters / 10) * 10} m` : `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const primaryButton = 'inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
const chipButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
export function CollecteMessage({
  kind,
  live,
  passedAt,
  onConfirmPickedUp,
  onReportNotPickedUp,
  onFindNearest
}: CollecteMessageProps) {
  const {
    minutes,
    arrived
  } = useLiveTruck();
  const materialLabel = materials[liveCollection.material].label;
  if (kind === 'batterie') {
    return <div data-id="element-849" data-mp="h013b4vjaoat">
        <p className={eyebrow} data-id="element-850" data-mp="h0aqylt3wjdc">
          <BatteryWarningIcon className="h-4 w-4" aria-hidden="true" data-id="element-851" data-mp="gx53fb04lpjq" />
          Piles et batteries
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-852" data-mp="7y9x7yx30fgf">
          Non, les piles et batteries ne vont jamais dans le bac de recyclage.
        </p>
        <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600" data-id="element-853" data-mp="bzddunmyefip">
          Écrasées dans le camion, elles peuvent provoquer un incendie. Apportez-les plutôt dans un point de dépôt
          : boîtes à piles municipales, écocentre ou commerces participants. Mettez un ruban adhésif sur les bornes
          des batteries au lithium avant de les déposer.
        </p>
        {live && <button type="button" onClick={onFindNearest} className={`mt-4 ${primaryButton}`} data-id="element-854" data-mp="3thslvqekztub">
            <NavigationIcon className="h-4 w-4" aria-hidden="true" data-id="element-855" data-mp="c95ppd0w5gyv" />
            Trouver le lieu le plus proche
          </button>}
      </div>;
  }
  if (kind === 'batterie-lieux') {
    const [nearest, ...others] = sortedDropOffs;
    return <div data-id="element-856" data-mp="h1fbcguz3uvc">
        <p className={eyebrow} data-id="element-857" data-mp="h0ngx9d4436n">
          <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-858" data-mp="h2gqawf411ic" />
          Points de dépôt près de chez vous
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-859" data-mp="v181ob2l892s">
          Le plus proche : {nearest.place.name}, à {formatDistance(nearest.meters)}.
        </p>

        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200" data-id="element-860" data-mp="1a2wyx2te5q6s">
          <div className="relative isolate" data-id="element-861" data-mp="1e51rdy83sgru">
            <DropOffMap places={batteryDropOffs} highlightId={nearest.place.id} data-id="element-862" data-mp="1e3qnlyenr90q" />
            <Link to={`/plan?filtre=piles&lieu=${nearest.place.id}`} className="absolute bottom-2.5 right-2.5 z-[500] inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-bold text-ville-navy shadow-md shadow-ville-navy/10 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-863" data-mp="1ixl4ku2vp9w4">
              <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-864" data-mp="155woygtf83ae" />
              Plan de la ville
            </Link>
          </div>
          <ul className="divide-y divide-slate-100 border-t border-slate-100 bg-white" data-id="element-865" data-mp="2bkp8evq8ji6b">
            {[nearest, ...others.slice(0, 3)].map(({
            place,
            meters
          }, index) => <li key={place.id} className={`group flex items-center justify-between gap-3 px-4 py-2.5 ${index === 0 ? 'bg-ville-sand' : ''}`} data-id="element-866" data-mp="4vdkelpsynme" data-mpi={index}>
                <span className="min-w-0" data-id="element-867" data-mp="3rb826h4b98jg">
                  <span className={`block truncate text-sm text-ville-navy ${index === 0 ? 'font-extrabold' : 'font-bold'}`} data-id="element-868" data-mp="3r9wxmf31jwra">
                    {place.name}
                  </span>
                  {index === 0 && <span className="block truncate text-[13px] text-slate-600" data-id="element-869" data-mp="3i98j7j9qjudv">{place.address}</span>}
                  <span className="block text-xs" data-id="element-870" data-mp="3deuw04588tka">
                    <span className={`font-bold ${place.open ? 'text-ville-deep' : 'text-slate-400'}`} data-id="element-871" data-mp="38egkud8bx3i1">
                      {place.open ? 'Ouvert' : 'Fermé'}
                    </span>
                    <span className="tabular-nums text-slate-500" data-id="element-872" data-mp="16malnmol9ykq"> · {formatDistance(meters)}</span>
                  </span>
                </span>
                <a href={`https://www.openstreetmap.org/directions?route=${profileCoordinates[0]}%2C${profileCoordinates[1]}%3B${place.lat}%2C${place.lng}`} target="_blank" rel="noreferrer" aria-label={`Itinéraire vers ${place.name}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ville-navy px-3 py-1.5 text-xs font-bold text-white opacity-0 transition-opacity duration-150 ease-ville focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 group-hover:opacity-100 [@media(hover:none)]:opacity-100" data-id="element-873" data-mp="11kuynp2uphzp">
                  <NavigationIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-874" data-mp="35sppupwm0c8d" />
                  Itinéraire
                </a>
              </li>)}
          </ul>
        </div>
      </div>;
  }
  if (kind === 'camion-en-route') {
    return <div data-id="element-875" data-mp="1yfnv40jxv05a">
        <p className={eyebrow} data-id="element-876" data-mp="1yggxyqfs15i5">
          <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-877" data-mp="1ydb46xw7dfg3" />
          Collecte en cours
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-878" data-mp="1qxzg9wxqyke3">
          Pas d’inquiétude : le camion n’est pas encore passé chez vous.
        </p>
        <p className="mt-1 text-[15px] leading-relaxed text-slate-600" data-id="element-879" data-mp="20ucb4y5134af">
          {arrived ? 'Le camion vient d’arriver à votre adresse.' : `Le camion est en route et devrait arriver d’ici ${minutes} minute${minutes > 1 ? 's' : ''}.`}
        </p>

        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200" data-id="element-880" data-mp="136kb7wel5oz5">
          <TruckPreviewMap data-id="element-881" data-mp="134hc8mvgglia" />
          <dl className="grid grid-cols-[1fr_2fr] divide-x divide-slate-100 border-t border-slate-100 bg-white" data-id="element-882" data-mp="1hvq99h1ot95g">
            <div className="px-4 py-3" data-id="element-883" data-mp="1d3gmg9vvcp7e">
              <dt className="flex items-center gap-1.5 text-xs font-bold text-slate-500" data-id="element-884" data-mp="1d2exd1i0q26o">
                <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-885" data-mp="pd2t8g1jskb8" />
                Arrivée estimée
              </dt>
              <dd className="mt-0.5 text-lg font-extrabold tabular-nums text-ville-blue" data-id="element-886" data-mp="kdzuteg2pcc8">
                {arrived ? 'Sur place' : `${minutes} min`}
              </dd>
            </div>
            <div className="px-4 py-3" data-id="element-887" data-mp="3xot2w266e12">
              <dt className="flex items-center gap-1.5 text-xs font-bold text-slate-500" data-id="element-888" data-mp="8u5rcj3328rv">
                <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-889" data-mp="cw0uzu8hky8t" />
                Votre adresse
              </dt>
              <dd className="mt-0.5 truncate text-sm font-bold text-ville-navy" data-id="element-890" data-mp="hw57782d36t8">{citizenAddress}</dd>
            </div>
          </dl>
        </div>
      </div>;
  }
  if (kind === 'camion-passe') {
    return <div data-id="element-891" data-mp="rmhsx3lwx60y">
        <p className={eyebrow} data-id="element-892" data-mp="rlotyot73ktd">
          <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-893" data-mp="wk9vcrk6oaje" />
          Le camion est passé
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-894" data-mp="10lv5w698wcqf">
          Le camion est passé devant chez vous{passedAt ? ` à ${passedAt}` : ''}. Votre bac a-t-il bien été ramassé ?
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-2.5" data-id="element-895" data-mp="2q3i9kcy52xrj">
            <button type="button" onClick={onConfirmPickedUp} className={primaryButton} data-id="element-896" data-mp="2q0mbmomak933">
              Oui
            </button>
            <button type="button" onClick={onReportNotPickedUp} className={chipButton} data-id="element-897" data-mp="2h1six9c302ut">
              Non, il est toujours plein
            </button>
          </div>}
      </div>;
  }
  if (kind === 'bac-ok') {
    return <div data-id="element-898" data-mp="38sylrxj6sens">
        <p className={eyebrow} data-id="element-899" data-mp="38sotg3ao5vex">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-900" data-mp="34rva7n8694sd" />
          Collecte terminée
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-901" data-mp="300wou98btgi9">
          Parfait, merci de la confirmation. Vous pouvez rentrer votre bac. La prochaine collecte est prévue le{' '}
          {nextCollectionLabel}.
        </p>
      </div>;
  }
  if (kind === 'requete-ouverte') {
    return <div data-id="element-902" data-mp="3nr0ok5ie8w3z">
        <p className={eyebrow} data-id="element-903" data-mp="3nqh7ss4hcdrj">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-904" data-mp="3np67j2a8to56" />
          Requête ouverte auprès de la Ville
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-905" data-mp="2yynn7o79pd4c">
          Désolée ! J’ai ouvert une requête auprès de la Ville pour votre bac non ramassé.
        </p>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-ville-sand p-4" data-id="element-906" data-mp="37y0x2hzb03pl">
          <p className="text-xs font-bold text-slate-500" data-id="element-907" data-mp="33ul17nv267dy">Requête {binRequestReference}</p>
          <p className="mt-1 text-[15px] font-extrabold text-ville-navy" data-id="element-908" data-mp="3hungd2bzempi">Bac non ramassé — {materialLabel}</p>
          <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-909" data-mp="3cvay6uw223t9">
            <div className="flex items-center gap-1.5" data-id="element-910" data-mp="3rkyufhpdqca4">
              <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-911" data-mp="3rkfc3zvthlvk" />
              <dt className="sr-only" data-id="element-912" data-mp="3mmxeh5n92t2v">Adresse</dt>
              <dd data-id="element-913" data-mp="4fzilpzbzuqg">{citizenAddress}</dd>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-914" data-mp="4eg2q0hl7lp7">
              <Trash2Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-915" data-mp="3vnlkpe6ejp57" />
              <dt className="sr-only" data-id="element-916" data-mp="j2yafm23or5p">Collecte</dt>
              <dd data-id="element-917" data-mp="j14xxq4t3cps">Collecte du jour · {liveCollection.sector}</dd>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-918" data-mp="o1ss2koc09o8">
              <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-919" data-mp="a32tf0lv61xr" />
              <dt className="sr-only" data-id="element-920" data-mp="e3lmexzhte1j">Délai</dt>
              <dd data-id="element-921" data-mp="e2blm1199rr7">Rattrapage prévu dans les 48 heures ouvrables</dd>
            </div>
          </dl>
          <p className="mt-3 text-[13px] text-slate-600" data-id="element-922" data-mp="bf6nd09592m">Laissez votre bac au bord de la rue d’ici là.</p>
        </div>

        <NotifyFollowUp live={live} requestId={binRequest.id} data-id="element-923" data-mp="559oeawardz2" />

        {live && <Link to={`/mes-demandes/${binRequest.id}`} className={`mt-5 ${primaryButton}`} data-id="element-924" data-mp="3mlrftbkq69os">
            Suivre ma requête
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-925" data-mp="3dj9v51kpxp3w" />
          </Link>}
      </div>;
  }
  if (kind === 'suivi') {
    return <div data-id="element-926" data-mp="3ih1ps3cqzpbs">
        <p className={eyebrow} data-id="element-927" data-mp="3ihjr3ee9otqk">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-928" data-mp="1o24ldkv0l67j" />
          Collectes
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-929" data-mp="1jbefm6iof1cb">
          {arrived ? 'C’est noté. Si votre bac n’a pas été vidé, dites-le-moi et j’ouvre une requête auprès de la Ville.' : 'C’est noté. Le camion est toujours en route ; je vous demanderai si votre bac a été vidé dès son passage.'}
        </p>
      </div>;
  }
  return <div data-id="element-930" data-mp="2707dw9mrtla3">
      <p className={eyebrow} data-id="element-931" data-mp="26yw8y6hla7he">
        <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-932" data-mp="221e16jfb5yaz" />
        Collectes
      </p>
      <p className="mt-2 text-[15px] leading-relaxed text-ville-navy" data-id="element-933" data-mp="1y026w0lhoowg">
        Votre prochaine collecte est celle du jour ({materialLabel}, {liveCollection.sector}).{' '}
        {arrived ? 'Le camion est déjà passé à votre adresse.' : 'Le camion est en route : suivez-le en direct sur la carte.'}
      </p>
    </div>;
}